import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, Edit, Trash2, MapPin, Clock, X, Save, Upload, Link
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Image Upload Component with URL fallback
const ImageUploader = ({ value, onChange, uploadEndpoint, adminAuth, label = "Image" }) => {
  const [mode, setMode] = useState(value && value.startsWith('http') ? 'url' : 'upload');
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}${uploadEndpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${adminAuth}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        onChange(data.url);
        toast.success('Image uploaded');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium">{label}</label>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => setMode('upload')}
            className={`px-2 py-1 text-xs rounded ${mode === 'upload' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}
          >
            <Upload className="w-3 h-3 inline mr-1" />
            Upload
          </button>
          <button
            type="button"
            onClick={() => setMode('url')}
            className={`px-2 py-1 text-xs rounded ${mode === 'url' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}
          >
            <Link className="w-3 h-3 inline mr-1" />
            URL
          </button>
        </div>
      </div>

      {mode === 'upload' ? (
        <div className="space-y-2">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            disabled={uploading}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100 disabled:opacity-50"
          />
          {uploading && <p className="text-xs text-amber-600">Uploading...</p>}
        </div>
      ) : (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
        />
      )}

      {/* Preview */}
      {value && (
        <div className="relative w-full h-24 rounded-lg overflow-hidden bg-gray-100">
          <img
            src={getFullUrl(value)}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
    </div>
  );
};

const AdminDestinations = () => {
  const { adminAuth, fetchStats } = useOutletContext();
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    duration: '',
    type: '',
    activities: []
  });

  useEffect(() => {
    fetchDestinations();
  }, []);

  const fetchDestinations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/destinations`);
      const data = await response.json();
      setDestinations(data);
    } catch (error) {
      toast.error('Failed to fetch destinations');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId 
        ? `${API_URL}/api/admin/destinations/${editingId}`
        : `${API_URL}/api/admin/destinations`;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Basic ${adminAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingId ? 'Destination updated!' : 'Destination created!');
        resetForm();
        fetchDestinations();
        fetchStats();
      } else {
        toast.error('Operation failed');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleEdit = (destination) => {
    setFormData({
      name: destination.name,
      description: destination.description,
      image: destination.image,
      duration: destination.duration,
      type: destination.type,
      activities: destination.activities || []
    });
    setEditingId(destination.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this destination?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/destinations/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${adminAuth}`
        }
      });

      if (response.ok) {
        toast.success('Destination deleted');
        fetchDestinations();
        fetchStats();
      } else {
        toast.error('Delete failed');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      image: '',
      duration: '',
      type: '',
      activities: []
    });
    setEditingId(null);
    setShowForm(false);
  };

  const addActivity = () => {
    const newActivity = {
      id: formData.activities.length + 1,
      name: '',
      description: '',
      image: '',
      price: 0
    };
    setFormData({
      ...formData,
      activities: [...formData.activities, newActivity]
    });
  };

  const updateActivity = (index, field, value) => {
    const newActivities = [...formData.activities];
    newActivities[index][field] = value;
    setFormData({ ...formData, activities: newActivities });
  };

  const removeActivity = (index) => {
    const newActivities = formData.activities.filter((_, i) => i !== index);
    setFormData({ ...formData, activities: newActivities });
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Destinations</h1>
          <p className="text-gray-600">Manage travel destinations and activities</p>
        </div>
        <Button 
          onClick={() => setShowForm(true)} 
          className="bg-amber-600 hover:bg-amber-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Destination
        </Button>
      </div>

      {/* Destination Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingId ? 'Edit Destination' : 'Add Destination'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={resetForm}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Name</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g., Bali, Indonesia"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Duration</label>
                    <Input
                      name="duration"
                      value={formData.duration}
                      onChange={handleChange}
                      placeholder="e.g., 5-7 Days"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <Input
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    placeholder="e.g., Honeymoon, Leisure, Adventure"
                    required
                  />
                </div>

                {/* Destination Image Upload */}
                <ImageUploader
                  value={formData.image}
                  onChange={(url) => setFormData({ ...formData, image: url })}
                  uploadEndpoint="/api/admin/upload/destination"
                  adminAuth={adminAuth}
                  label="Destination Image"
                />

                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Describe this destination..."
                    required
                  />
                </div>

                {/* Activities Section */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold">Activities</h3>
                    <Button type="button" variant="outline" size="sm" onClick={addActivity}>
                      <Plus className="w-4 h-4 mr-1" /> Add Activity
                    </Button>
                  </div>
                  
                  <div className="space-y-4 max-h-80 overflow-y-auto">
                    {formData.activities.map((activity, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex justify-between items-start mb-3">
                          <span className="text-sm font-medium text-amber-600">Activity {index + 1}</span>
                          <button 
                            type="button" 
                            onClick={() => removeActivity(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                          <Input
                            value={activity.name}
                            onChange={(e) => updateActivity(index, 'name', e.target.value)}
                            placeholder="Activity name"
                          />
                          <Input
                            value={activity.description}
                            onChange={(e) => updateActivity(index, 'description', e.target.value)}
                            placeholder="Description"
                          />
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Price (INR, per person)</label>
                            <Input
                              type="number"
                              min="0"
                              value={activity.price ?? 0}
                              onChange={(e) => updateActivity(index, 'price', parseInt(e.target.value) || 0)}
                              placeholder="e.g. 2500"
                            />
                          </div>
                          {/* Activity Image Upload */}
                          <ImageUploader
                            value={activity.image}
                            onChange={(url) => updateActivity(index, 'image', url)}
                            uploadEndpoint="/api/admin/upload/activity"
                            adminAuth={adminAuth}
                            label="Activity Image"
                          />
                        </div>
                      </div>
                    ))}
                    {formData.activities.length === 0 && (
                      <p className="text-gray-500 text-sm text-center py-4">
                        No activities added yet. Click &quot;Add Activity&quot; to add one.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                    <Save className="w-4 h-4 mr-2" />
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Destinations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {destinations.map((destination) => (
          <Card key={destination.id} className="overflow-hidden hover:shadow-lg transition-shadow">
            <div className="relative h-40">
              <img
                src={getFullImageUrl(destination.image)}
                alt={destination.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                }}
              />
              <div className="absolute top-2 right-2 flex gap-1">
                <button
                  onClick={() => handleEdit(destination)}
                  className="p-2 bg-white rounded-full shadow hover:bg-gray-100"
                >
                  <Edit className="w-4 h-4 text-blue-600" />
                </button>
                <button
                  onClick={() => handleDelete(destination.id)}
                  className="p-2 bg-white rounded-full shadow hover:bg-gray-100"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>
            </div>
            <CardContent className="p-4">
              <h3 className="font-bold text-lg mb-2">{destination.name}</h3>
              <p className="text-gray-600 text-sm mb-3 line-clamp-2">{destination.description}</p>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center text-amber-600">
                  <Clock className="w-4 h-4 mr-1" />
                  {destination.duration}
                </div>
                <div className="flex items-center text-gray-500">
                  <MapPin className="w-4 h-4 mr-1" />
                  {destination.activities?.length || 0} activities
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {destinations.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            No destinations yet. Click &quot;Add Destination&quot; to create one.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDestinations;
